import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  BackendResponse,
  Bid,
  Datastore,
  DatastoreDocument,
  Milestone,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { LinkColor, LinkHoverColor } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType, TextAlign } from '@freelancer/ui/text';
import { requiredTruthy } from '@freelancer/ui/validators';
import * as Rx from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';

type ReleaseMilestoneRedirectSchema =
  | {
      type: 'COMPLETE';
      bidderId: number;
    }
  | {
      type: 'RELEASE';
    };

@Component({
  template: `
    <fl-bit class="MilestoneReleaseModalBody">
      <fl-picture
        alt="Milestone release warning"
        i18n-alt="Milestone release warning image"
        [alignCenter]="true"
        [src]="'project-view/milestone-release-warning.svg'"
      ></fl-picture>
      <fl-heading
        i18n="Confirm release milestone warning heading"
        [size]="HeadingSize.XSMALL"
        [headingType]="HeadingType.H3"
        [flMarginBottom]="Margin.XSMALL"
      >
        Confirm Release of Funds
      </fl-heading>
      <fl-text
        class="Description"
        [flMarginBottom]="Margin.SMALL"
        i18n="Release milestone warning text"
        [fontType]="FontType.SPAN"
        [textAlign]="TextAlign.CENTER"
      >
        Only release milestone payments when you are 100% satisfied with your
        freelancer's work. They cannot be returned once released.
      </fl-text>
      <fl-checkbox
        [control]="checkControl"
        label="My freelancer has delivered satisfactory work."
        i18n-label="Release milestone checkbox text"
        [flMarginBottom]="Margin.LARGE"
      ></fl-checkbox>

      <fl-hr [flMarginBottom]="Margin.LARGE"></fl-hr>

      <fl-bit
        *ngIf="(submissionPromise | async)?.status === 'error'"
        [ngSwitch]="(submissionPromise | async)?.errorCode"
        [flMarginBottom]="Margin.SMALL"
      >
        <fl-text
          *ngSwitchCase="'PROFILE_INCOMPLETE'"
          [color]="FontColor.ERROR"
          i18n="Releasing milestone error that indicates incomplete profile"
        >
          You need to
          <fl-link
            [link]="'/support/Profile/how-to-edit-your-profile'"
            [newTab]="true"
            >complete your profile</fl-link
          >
          before releasing milestones.
        </fl-text>
        <fl-text
          *ngSwitchCase="'EMAIL_VERIFICATION_REQUIRED'"
          [color]="FontColor.ERROR"
          i18n="Releasing milestone error that indicates unverified email"
        >
          You need to
          <fl-link
            [link]="'/support/profile/verifying-my-email-address'"
            [newTab]="true"
            >verify your email address</fl-link
          >
          before releasing milestones.
        </fl-text>
        <fl-text
          *ngSwitchDefault
          [color]="FontColor.ERROR"
          i18n="General error message for releasing milestones"
        >
          An unknown error occurred while trying to award the freelancer. Please
          try again or contact support@freelancer.com
          <ng-container *ngIf="(submissionPromise | async)?.requestId">
            with request_id: {{ (submissionPromise | async)?.requestId }}
          </ng-container>
        </fl-text>
      </fl-bit>

      <fl-button
        [color]="ButtonColor.SECONDARY"
        [size]="ButtonSize.SMALL"
        i18n="Release milestone button"
        [flMarginBottom]="Margin.XSMALL"
        [busy]="submissionPromise && (submissionPromise | async) === null"
        [disabled]="!(canRelease$ | async)"
        (click)="releasePayment()"
      >
        Release Payment
      </fl-button>
      <fl-link
        i18n="Close release milestone modal"
        [hoverColor]="LinkHoverColor.DEFAULT"
        [color]="LinkColor.DARK"
        (click)="closeModal()"
      >
        Cancel
      </fl-link>
    </fl-bit>
  `,
  styleUrls: ['./milestone-release-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MilestoneReleaseModalComponent implements OnInit {
  HeadingType = HeadingType;
  HeadingSize = HeadingSize;
  FontColor = FontColor;
  FontType = FontType;
  Margin = Margin;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  LinkHoverColor = LinkHoverColor;
  LinkColor = LinkColor;
  TextAlign = TextAlign;

  @Input() milestoneDoc: DatastoreDocument<'milestones'>;
  @Input() milestone: Milestone;
  @Input() bid: Bid;

  checkControl = new FormControl(false, requiredTruthy(''));
  canRelease$: Rx.Observable<boolean>;
  paidMilestone$: Rx.Observable<number>;
  isFixedBid$: Rx.Observable<boolean>;

  errorCode: string | undefined;
  requestId: string | undefined;
  submissionPromise: Promise<BackendResponse>;

  constructor(
    private datastore: Datastore,
    private modalRef: ModalRef<MilestoneReleaseModalComponent>,
  ) {}

  ngOnInit() {
    this.canRelease$ = this.checkControl.statusChanges.pipe(
      startWith(this.checkControl.status),
      map(status => status === 'VALID'),
    );

    const milestoneCollection = this.datastore.collection('milestones', query =>
      query
        .where('projectId', '==', this.milestone.projectId)
        .where('bidId', '==', this.milestone.bidId)
        .where('bidderId', '==', this.milestone.bidderId),
    );

    this.paidMilestone$ = milestoneCollection
      .valueChanges()
      .pipe(
        map(milestones =>
          milestones
            .filter(m => m.status === 'cleared')
            .reduce((a, b) => a + (b.amount || 0), 0),
        ),
      );

    const hourlyContractsCollection = this.datastore.collection(
      'hourlyContracts',
      query =>
        query
          .where('projectId', '==', this.milestone.projectId)
          .where('bidderId', '==', this.milestone.bidderId)
          .where('active', '==', true),
    );

    this.isFixedBid$ = hourlyContractsCollection
      .valueChanges()
      .pipe(map(hourlyContracts => hourlyContracts.length === 0));
  }

  releasePayment() {
    this.submissionPromise = Promise.all([
      this.milestoneDoc.update(this.milestone),
      this.paidMilestone$.pipe(take(1)).toPromise(),
      this.isFixedBid$.pipe(take(1)).toPromise(),
    ]).then(([response, paidAmount, isFixed]) => {
      if (response.status === 'success') {
        // Milestone total amount >= bid amount, COMPLETE redirect to review tab
        if (
          isFixed &&
          paidAmount + (this.milestone.amount || 0) >= this.bid.amount
        ) {
          this.closeModal({ type: 'COMPLETE', bidderId: this.bid.bidderId });
          return response;
        }
        this.closeModal({ type: 'RELEASE' });
        return response;
      }
      return response;
    });
  }

  // undefined for close without releasing
  // 'Complete' for release and project complete
  // 'Release' for just releasing without side effects
  closeModal(param?: ReleaseMilestoneRedirectSchema) {
    this.modalRef.close(param);
  }
}
